import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconComponent, IconName } from "@hopper-b2b/ui";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import { Box, Fab } from "@mui/material";
import clsx, { type ClassValue } from "clsx";
import { type ChatProvider, testChatProvider } from "../../types/ChatProvider";
import { ChatWindow } from "../ChatWindow";
import {
  getIsChatFabVisible,
  getIsMinimized,
  minimizeChatWindow,
  toggleChatWindow,
} from "../../store";

import styles from "./ChatFab.module.scss";
import { DeepLink } from "@b2bportal/chat-api";

export interface IChatFabProps {
  chatProvider?: ChatProvider;
  className?: ClassValue;
  hideChatFabIcon?: string;
  showChatFabIcon?: string;
  handleDeepLink: (deepLink: DeepLink) => void;
}

const ChatFab: React.FC<IChatFabProps> = (props) => {
  const {
    chatProvider = testChatProvider,
    className,
    hideChatFabIcon,
    showChatFabIcon,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const isMinimized = useSelector(getIsMinimized);
  const isFabVisible = useSelector(getIsChatFabVisible);

  const getHideFabIcon = () => {
    const className = clsx("fab-icon", styles.fabIcon);

    if (hideChatFabIcon) {
      return <img alt="Hide Chat" src={hideChatFabIcon} />;
    }

    return (
      <IconComponent className={className} name={IconName.RoundArrowDown} />
    );
  };

  const getShowFabIcon = () => {
    const className = clsx("fab-icon", styles.fabIcon);

    if (showChatFabIcon) {
      return <img alt="Show Chat" src={showChatFabIcon} />;
    }

    return <IconComponent className={className} name={IconName.SupportChat} />;
  };

  const handleOnClick = () => {
    dispatch(toggleChatWindow());
  };

  const minimizeChat = useCallback(() => {
    dispatch(minimizeChatWindow());
  }, [dispatch]);

  return (
    <Box
      className={clsx(
        className,
        "chat-fab-container",
        {
          [styles.mobileOpen]: matchesMobile && !isMinimized,
          [styles.open]: !isMinimized,
        },
        styles.chatFabContainer
      )}
    >
      <ChatWindow
        chatProvider={chatProvider}
        minimizeChat={minimizeChat}
        mobile={matchesMobile}
        open={!isMinimized}
        handleDeepLink={props.handleDeepLink}
      />
      <Fab
        aria-label="help"
        className={clsx(
          "fab-button",
          {
            [styles.showFab]: isFabVisible,
          },
          styles.fabButton
        )}
        onClick={handleOnClick}
        variant={"circular"}
      >
        {isMinimized ? getShowFabIcon() : getHideFabIcon()}
      </Fab>
    </Box>
  );
};

export default ChatFab;
